/**
 * mapSlaResponse — pure mapping layer.
 *
 * Turns raw checkDeliveryDetails (v2) SLA options into render-ready view models.
 * Contains NO React and NO rendering concerns so it can be unit-tested in
 * isolation and reused by PDP / Cart on desktop. The presentation components
 * depend only on the SlaOption / SlaViewModel view models produced here.
 */
import {
  DeliveryMethod,
  PriceType,
  PriceTypeTags,
  RawProductServiceability,
  RawSkuEntry,
  RawSlaOption,
  RawSlaResponse,
  SlaOption,
  SlaViewModel
} from './types';
import { hasBadgePriceType } from '../../../refresh/molecules/DeliverySLA/constants';

const toNumber = (value?: string): number => {
  const parsed = parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const tagFor = (priceType: PriceType, tags?: PriceTypeTags): string =>
  tags?.[priceType] || priceType.toUpperCase();

/**
 * Map scopedView API priceType → UI / Amplitude priceTypeTags keys.
 * BEST_PRICE → best, BEST_SPEED → fastest (also accepts legacy best/fastest).
 * Used for PDP scopedView; cart deliveryChoices typically already send best/fastest.
 */
const normalizePriceType = (priceType?: string): PriceType => {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (key === 'BEST_SPEED' || key === 'FASTEST') {
    return 'fastest';
  }
  return 'best';
};

const normalizeDeliveryMethod = (method?: string): DeliveryMethod | undefined => {
  const upper = String(method || '').toUpperCase();
  if (upper === 'RUSH') return 'RUSH';
  if (upper === 'PRIORITY') return 'PRIORITY';
  if (upper === 'STANDARD') return 'STANDARD';
  return undefined;
};

/** Payloads carry either `serviceability` or the misspelled `servicability`. */
const isServiceable = (entry?: { serviceability?: boolean; servicability?: boolean }): boolean =>
  Boolean(entry?.serviceability ?? entry?.servicability);

/**
 * Pick the productDetails entry that belongs to this option.
 *
 * An option can carry several entries (e.g. STANDARD alongside RUSH). Taking
 * the first serviceable one makes a "fastest" option render the STANDARD date
 * and lose the cutoff, so match the entry to the priceType first. Serviceable
 * entries win; a non-serviceable one is still returned so the UI can show why.
 */
const pickProductDetail = (
  details: RawProductServiceability[],
  priceType: PriceType
): RawProductServiceability | undefined => {
  const pool = details.filter(isServiceable);
  const candidates = pool.length ? pool : details;

  const matchesPriceType = (detail: RawProductServiceability): boolean => {
    const method = normalizeDeliveryMethod(detail?.deliveryMethod);
    return priceType === 'fastest'
      ? method === 'RUSH' || method === 'PRIORITY'
      : method === 'STANDARD';
  };

  return candidates.find(matchesPriceType) ?? candidates[0];
};

/**
 * Map a single raw option to a view model. The matched product entry drives
 * delivery + cod; it falls back to the non-serviceable entry so the UI can
 * still show a "not serviceable" reason.
 */
export const mapSlaOption = (
  raw: RawSlaOption,
  tags?: PriceTypeTags,
  /** Position in the list — part of the card's identity, see `id` below. */
  index = 0
): SlaOption => {
  const details = raw.serviceabilityDetails?.productDetails ?? [];
  const priceType = normalizePriceType(raw.priceType);
  const product = pickProductDetail(details, priceType);

  const serviceable = isServiceable(raw.serviceabilityDetails) && isServiceable(product);
  const fee = toNumber(product?.deliveryFee);

  const priceData = raw.price?.priceData;
  const wasPriceData = raw.price?.wasPriceData;

  const stockStatus = raw.stock?.stockLevelStatus;

  return {
    priceType,
    // Sent as-is on cart/wishlist requests — never the normalised key.
    rawPriceType: raw.priceType || '',
    // B2C attributes the cart entry by seller id rather than by priceType.
    sellerId: raw.sellerId || '',
    // Per-option copy wins (seller list); otherwise the priceType lookup. An
    // explicit "" is honoured — that is how an untagged seller row asks for no
    // badge while still normalising to a priceType. Combined types
    // (BEST_PRICE_SPEED / BEST_SPEED_PRICE) and rows with no priceType get no
    // badge: `normalizePriceType` would otherwise collapse them onto "best"
    // and show BEST PRICE.
    tagLabel:
      raw.tagLabel ??
      (hasBadgePriceType(raw.priceType) ? tagFor(priceType, tags) : ''),
    badgeType: raw.badgeType,
    isSelected: Boolean(raw.isSelected),
    priceGroupId: raw.priceGroupId,
    serviceable,
    reasonForNotServiceability: product?.reasonForNotServiceability,
    codEligible: Boolean(raw.serviceabilityDetails?.codEligible ?? product?.codEligible),
    price: {
      current: priceData?.displayformattedValue || priceData?.formattedValue,
      was: wasPriceData?.displayformattedValue || wasPriceData?.formattedValue,
      discount: priceData?.discountPercent
    },
    delivery: {
      deliveryMethod: normalizeDeliveryMethod(product?.deliveryMethod),
      deliverySLA: product?.deliverySLA,
      eddUpper: product?.eddUpper,
      cutOffTime: product?.cutOffTime,
      fee,
      isFree: fee === 0
    },
    stock: {
      status: stockStatus,
      level: raw.stock?.stockLevel,
      isLowStock: stockStatus === 'lowStock'
    },
    sellerName: raw.sellerName || product?.sellerName,
    // Some responses carry the seller only on the product detail.
    sellerId: raw.sellerId || product?.sellerId,
    raw,
    /**
     * Card identity for selection and the React key.
     *
     * Always carries the position, because nothing else on the option is
     * reliably unique: B2C lists every seller for the size and they routinely
     * share a `priceGroupId`, so several cards read as the same card — the wrong
     * row highlights and the pick cannot be told apart. Keying off the seller
     * instead is not enough either: `sellerId` is sometimes sent only on the
     * option's product detail.
     */
    id: `${raw.priceGroupId || raw.priceType || 'sla'}-${index}`
  };
};

export interface MapSlaOptionsConfig {
  /**
   * Collapse options sharing a priceType (default). The seller list turns this
   * off: every seller is its own card, and they nearly all normalise to "best",
   * so de-duping would leave a single row.
   */
  dedupeByPriceType?: boolean;
}

/**
 * Map a list of raw options (belonging to one sku) into the view model.
 * De-dupes by priceType (keeps first) unless told otherwise. Order is left
 * exactly as the API sent it — the backend owns the card order, matching the
 * msite mapper.
 */
export const mapSlaOptions = (
  rawOptions: RawSlaOption[] | undefined,
  tags?: PriceTypeTags,
  { dedupeByPriceType = true }: MapSlaOptionsConfig = {}
): SlaViewModel => {
  const options = (rawOptions ?? [])
    // Keep options the API sent without a priceType — they normalise to "best"
    // rather than disappearing from the list.
    .filter(Boolean)
    .reduce<SlaOption[]>((acc, raw) => {
      if (dedupeByPriceType && acc.some(o => o.priceType === normalizePriceType(raw.priceType))) {
        return acc;
      }
      acc.push(mapSlaOption(raw, tags, acc.length));
      return acc;
    }, []);

  // Sole serviceable card is always the selected option.
  const serviceable = options.filter(option => option.serviceable);
  if (serviceable.length === 1) {
    const onlyId = serviceable[0].priceGroupId;
    options.forEach(option => {
      option.isSelected = option.serviceable && option.priceGroupId === onlyId;
    });
  }

  return {
    options,
    isMulti: options.length > 1,
    isEmpty: options.length === 0
  };
};

/**
 * Extract the raw SLA options for a given sku (productCode) from the v2
 * response. The v2 `sku` array holds single-key maps { [productCode]: [...] }.
 * When skuCode is omitted the first entry is returned (PDP typically has one
 * sku in the response).
 */
export const getSlaOptionsForSku = (
  response: RawSlaResponse | undefined,
  skuCode?: string
): RawSlaOption[] => {
  const entries: RawSkuEntry[] = response?.sku ?? [];
  if (entries.length === 0) return [];

  if (skuCode) {
    const match = entries.find(entry => entry && entry[skuCode]);
    if (match) return match[skuCode] ?? [];
  }

  const firstKey = Object.keys(entries[0] ?? {})[0];
  return (firstKey && entries[0][firstKey]) || [];
};

/** Convenience: response + sku + tags → view model, in one call. */
export const mapSlaResponse = (
  response: RawSlaResponse | undefined,
  skuCode?: string,
  tags?: PriceTypeTags
): SlaViewModel => mapSlaOptions(getSlaOptionsForSku(response, skuCode), tags);
